import * as React from 'react';
import { useEffect, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { IconName, ValidationBarTheme } from '../../enums';
import { SuggestionItem } from '../../interfaces';
import { UseState } from '../../types';
import { Icon } from '../icon/icon.component';
import { Input } from '../input/input.component';

import { 
  Container, 
  ImageContainer, 
  ImageElement, 
  SuggestionElement, 
  SuggestionLabel, 
  SuggestionsList, 
  StyledValidationBar 
} from './autosuggest.styled';
import { AutosuggestProps } from './autosuggest.types';

export const Autosuggest: React.FC<AutosuggestProps> = (props: AutosuggestProps): JSX.Element => {
  const { t }: TransProps<never> = useTranslation();
  const {
    label,
    suggestions,
    onValueInputChange,
    onValueInputChangeAsync,
    onSelect,
    onSelectNotSuggestedValuePromise,
    onClear,
    initialSuggestion,
    initialSuggestionLabel,
    withAcceptNotSuggestedValue,
    disabled = false,
    iconName = IconName.Image,
    className
  }: AutosuggestProps = props;
  const [value, setValue]: UseState<string> = useState<string>('');
  const [inputValue, setInputValue]: UseState<string> = useState<string>('');
  const [isTyping, setIsTyping]: UseState<boolean> = useState<boolean>(false);
  const [localSuggestions, setLocalSuggestions]: UseState<SuggestionItem[]> = useState<SuggestionItem[]>(suggestions ?? []);
  const [validationMessage, setValidationMessage]: UseState<string> = useState<string>('');

  const onSelectValue: (suggestion: SuggestionItem) => void = (suggestion: SuggestionItem): void => {
    setLocalSuggestions([]);
    setValue(suggestion.label);
    onSelect(suggestion);
    setIsTyping(false);
    setValidationMessage('');
  };

  const onSelectNotSuggestedValue: () => void = (): void => {
    const onSelectNotSuggestedValueAction: () => void = (): void => {
      onSelect({ label: inputValue, id: null });
      setIsTyping(false);
      setValidationMessage('');
    };

    if (onSelectNotSuggestedValuePromise) {
      void onSelectNotSuggestedValuePromise().then(onSelectNotSuggestedValueAction);
    } else {
      onSelectNotSuggestedValueAction();
    }
  };

  const onValueInputChangeAction: (textValue: string) => void = (textValue: string): void => {
    if (onValueInputChangeAsync) {
      onValueInputChangeAsync(textValue)
        .then(setLocalSuggestions)
        .catch((): void => setLocalSuggestions([]));
    } else if (onValueInputChange) {
      onValueInputChange(textValue);
    }
    setInputValue(textValue);
    setIsTyping(true);
  };

  const onValueInputBlurAction: (textValue: string) => void = (textValue: string): void => {
    if (onClear && textValue === '') {
      onClear();
      setIsTyping(false);
      setLocalSuggestions([]);
    }
  };

  const onClearIconClick: () => void= (): void => {
    if (onClear && value) {
      onClear();
      setIsTyping(false);
      setLocalSuggestions([]);
      setValue('');
    }
  };

  useEffect(
    (): void => {
      if (suggestions) {
        setLocalSuggestions(suggestions);
      }
    },
    [suggestions]
  );

  useEffect(
    (): void => {
      if (initialSuggestionLabel) {
        setValue(initialSuggestionLabel);
      } else if (initialSuggestion) {
        setValue(initialSuggestion.label);
      }
    },
    [initialSuggestion, initialSuggestionLabel]
  );

  const onPreventActionOnEnterClick: (e: React.KeyboardEvent<HTMLInputElement>) => void = (
    e: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  return (
    <Container className={className}>
      <StyledValidationBar message={t(validationMessage)} barTheme={ValidationBarTheme.Information} />
      <Input
        label={label}
        onChange={onValueInputChangeAction}
        value={value}
        disabled={disabled}
        onBlur={onValueInputBlurAction}
        onKeyPress={onPreventActionOnEnterClick}
        icon={(onClear && value) ? IconName.CloseSmallest : undefined}
        onIconClickAction={onClearIconClick}
      />
      {!localSuggestions.length ? (
        <SuggestionsList $isVisible={isTyping}>
          <SuggestionElement 
            onClick={
              withAcceptNotSuggestedValue && !!inputValue.match(/^[^\s]+(\s+[^\s]+)*$/) 
                ? onSelectNotSuggestedValue 
                : (): void => setValidationMessage('onarte.ui.validationBar.correctName')
            }
          >
            <SuggestionLabel>{withAcceptNotSuggestedValue 
              ? t('onarte.ui.autosuggest.notSuggestedValue', { value: inputValue })
              : t('onarte.ui.autosuggest.notFound')}
            </SuggestionLabel>
          </SuggestionElement>
        </SuggestionsList>
      ) : (
        <SuggestionsList $isVisible={!!localSuggestions.length}>
          {localSuggestions.slice(0, 5).map((suggestion: SuggestionItem, index: number): JSX.Element => (
            <SuggestionElement
              onClick={(): void => onSelectValue(suggestion)}
              key={`${suggestion.id ?? ''}${index}`}
            >
              {suggestion.image !== null && (
                <ImageContainer>
                  {suggestion.image ? (
                    <ImageElement src={suggestion.image} alt={suggestion.label} />
                  ) : (
                    <Icon name={iconName} size={24} />
                  )}
                </ImageContainer>
              )}
              <SuggestionLabel>{suggestion.label}</SuggestionLabel>
            </SuggestionElement>
          ))}
        </SuggestionsList>
      )}
    </Container>
  );
};
